// src/lib/menage-photo-queue.js — Phase 4 PR 4 (ADR-076)
//
// Queue locale IndexedDB pour photos preuve ménage / incident côté
// terrain (réseau pourri). Pattern :
//   1. Photo prise → enqueue local IDB (immédiat, pas de réseau)
//   2. Hook useMenagePhotoQueue tente upload Supabase Storage en BG
//   3. Retry exponentiel 1s/5s/30s sur fail réseau, max 3 tentatives
//   4. Après 3 échecs : POST /api/send-email type menage_photo_upload_failed → notif admin
//   5. Quand upload OK : remove de la queue, callback → caller patch
//      menage_runs.photos JSONB avec l'URL finale
//
// Pas de dépendance externe (idb-keyval/dexie évités). Wrapper IDB raw
// minimaliste (~80 LOC).
//
// PR #153 (R-1 fix) — extension mini-queue incident pré-INSERT côté ménage :
//   - enqueueIncidentPhoto({...}) : upload direct Storage AVANT INSERT
//     incident DB (le ménage doit pouvoir uploader 2+ photos avant que
//     l'incident existe en base). Path stable :
//       {logementId}/incidents/run-{runId}/incident-{tmpIncidentId}/{ts}.{ext}
//     tmpIncidentId généré côté client au mount du modal (1 par session).
//     Retry x3 sur erreur réseau.
//   - cleanupIncidentPhotos({...}) : best-effort delete batch (cancel modal
//     ou crash → cleanup orphelines). Tolérant aux erreurs.
//
// Note : ce flow ne passe PAS par IDB queue (upload synchrone session
// modal). Si network blip pendant le modal → erreur remontée à l'UI,
// l'utilisateur réessaie. Les orphelines crash navigateur sont gérées
// par BACKLOG-5 (cleanup mensuel cron).

import { supabase } from '../supabase'
import { compressImage } from './image-compression'
import { extraireExif } from './exif-light'

const DB_NAME = 'fbz-menage-photos'
const STORE = 'queue'
const DB_VERSION = 1
// Chantier photo Lot 2 (ADR-209) : les photos d'INCIDENT basculent du bucket
// PUBLIC `logements-photos` vers le bucket PRIVÉ `menage-photos` (servies en
// signed URL, comme les photos de pièce du Lot 1). Le path est déjà préfixé
// `{logement_id}/incidents/...` → couvert par les policies menage-photos
// (INSERT/SELECT ménage + admin_all). Bucket privé ⇒ pas d'URL publique :
// `enqueueIncidentPhoto` renvoie `publicUrl: null` et on signe à l'affichage.
const STORAGE_BUCKET = 'menage-photos'
const INCIDENT_UPLOAD_MAX_ATTEMPTS = 3

// ── Wrapper IDB raw ─────────────────────────────────────────────────

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function idbPut(item) {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(item)
    tx.oncomplete = () => { db.close(); resolve(item) }
    tx.onerror = () => { db.close(); reject(tx.error) }
  })
}

async function idbGet(id) {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(id)
    req.onsuccess = () => { db.close(); resolve(req.result || null) }
    req.onerror = () => { db.close(); reject(req.error) }
  })
}

async function idbAll() {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).getAll()
    req.onsuccess = () => { db.close(); resolve(req.result || []) }
    req.onerror = () => { db.close(); reject(req.error) }
  })
}

async function idbDelete(id) {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).delete(id)
    tx.oncomplete = () => { db.close(); resolve() }
    tx.onerror = () => { db.close(); reject(tx.error) }
  })
}

// ── API publique ────────────────────────────────────────────────────

function newPhotoId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Enqueue une photo locale. Appel UI au moment où l'utilisateur
 * sélectionne le fichier (capture caméra). Retourne l'item.
 *
 * @param {Object} params
 * @param {string} params.runId         — id menage_run associé
 * @param {string} params.logementId    — pour path Storage
 * @param {string} params.pieceId       — id pièce checklist (ou 'incident:<id>')
 * @param {Blob} params.file            — File/Blob image
 * @param {string} params.kind          — 'piece' | 'incident'
 */
export async function enqueuePhoto({ runId, logementId, pieceId, file, kind = 'piece' }) {
  const id = newPhotoId()
  const item = {
    id,
    runId,
    logementId,
    pieceId,
    kind,
    file, // Blob — IDB sait stocker des Blob
    fileName: file.name || `${id}.jpg`,
    mimeType: file.type || 'image/jpeg',
    status: 'queued', // queued | uploading | uploaded | failed
    attempts: 0,
    lastError: null,
    publicUrl: null,
    createdAt: new Date().toISOString(),
  }
  await idbPut(item)
  return item
}

export async function getQueueForRun(runId) {
  const all = await idbAll()
  return all.filter(it => it.runId === runId)
}

export async function getAllQueued() {
  const all = await idbAll()
  return all.filter(it => it.status === 'queued' || it.status === 'failed')
}

export async function markUploading(id) {
  const item = await idbGet(id)
  if (!item) return null
  item.status = 'uploading'
  item.attempts = (item.attempts || 0) + 1
  item.statusAt = new Date().toISOString()
  await idbPut(item)
  return item
}

export async function markUploaded(id, publicUrl) {
  const item = await idbGet(id)
  if (!item) return null
  item.status = 'uploaded'
  item.publicUrl = publicUrl
  item.lastError = null
  item.statusAt = new Date().toISOString()
  await idbPut(item)
  return item
}

export async function markFailed(id, errorMsg) {
  const item = await idbGet(id)
  if (!item) return null
  item.status = 'failed'
  item.lastError = String(errorMsg).slice(0, 500)
  item.statusAt = new Date().toISOString()
  await idbPut(item)
  return item
}

// Échec DÉFINITIF, écrit DANS la file avant l'alerte admin (lot « travail
// perdu »). Statut `final_failed` = exclu de getAllQueued : plus de nouvel
// essai d'envoi. L'item reste en IndexedDB (inspection manuelle possible).
// L'ALERTE est suivie à part : `notifie_le` n'est posé QUE quand l'envoi du
// courriel a réussi (markNotified) ; tant qu'il manque, l'alerte repart, au
// plus MAX_ALERTES fois (alerte_essais).
export const MAX_ALERTES = 3

export async function markFinalFailed(id, errorMsg) {
  const item = await idbGet(id)
  if (!item) return null
  item.status = 'final_failed'
  item.lastError = String(errorMsg).slice(0, 500)
  item.statusAt = new Date().toISOString()
  await idbPut(item)
  return item
}

export async function markAlerteEssai(id) {
  const item = await idbGet(id)
  if (!item) return null
  item.alerte_essais = (item.alerte_essais || 0) + 1
  await idbPut(item)
  return item
}

export async function markNotified(id) {
  const item = await idbGet(id)
  if (!item) return null
  item.notifie_le = new Date().toISOString()
  await idbPut(item)
  return item
}

/** Photos en échec définitif dont l'alerte n'est pas encore partie. */
export async function getAlertesEnAttente() {
  const all = await idbAll()
  return all.filter((it) => it.status === 'final_failed' && !it.notifie_le
    && (it.alerte_essais || 0) < MAX_ALERTES)
}

/**
 * Photo BLOQUÉE : restée « en cours d'envoi » ou « fichier envoyé » (onglet
 * fermé, téléphone en veille au milieu d'un passage) depuis plus de maxAgeMs.
 * Ces statuts sont exclus de getAllQueued : sans reprise, la photo ne
 * repartait jamais. PUR (testable sans IndexedDB).
 */
export function estBloquee(item, now, maxAgeMs) {
  if (!item || (item.status !== 'uploading' && item.status !== 'uploaded')) return false
  const depuis = Date.parse(item.statusAt || item.createdAt || '')
  if (!Number.isFinite(depuis)) return true // âge inconnu : on reprend
  return now - depuis > maxAgeMs
}

/** Remet en échec (donc à retenter) les photos bloquées. Renvoie leur nombre.
 *  Le renvoi est sans doublon : upsert sur le même chemin + commit idempotent. */
export async function remettreEnEchecLesBloquees(maxAgeMs) {
  const all = await idbAll()
  let n = 0
  for (const it of all) {
    if (!estBloquee(it, Date.now(), maxAgeMs)) continue
    const ancien = it.status
    it.status = 'failed'
    it.lastError = `Reprise : photo bloquée « ${ancien} » depuis plus de ${Math.round(maxAgeMs / 1000)} s`
    it.statusAt = new Date().toISOString()
    await idbPut(it)
    n += 1
  }
  return n
}

export async function removeFromQueue(id) {
  await idbDelete(id)
}

/**
 * Path Storage final.
 *
 * 3 patterns (PR #153 B-3 corrigé) :
 *   1. kind='piece' :
 *      {logement_id}/runs/{run_id}/{piece_id}-{photo_id}.{ext}
 *
 *   2. kind='incident' avec runId fourni (source='run' ménage) :
 *      {logement_id}/incidents/run-{run_id}/{photo_id}.{ext}
 *      → path stable lié FK incident.run_id (cleanup BACKLOG-5 simplifié)
 *      → résiste au cancel UI (orphelines reliées au run, pas tmpId)
 *
 *   3. kind='incident' admin manuel (pas de runId, item.pieceId = incident UUID) :
 *      {logement_id}/incidents/admin-{incidentId}/{photo_id}.{ext}
 *      → 2-phase upload : INSERT incident → récupère id → upload photos
 *        avec ce id → UPDATE incident.photos
 */
export function buildStoragePath(item) {
  const ext = (item.mimeType || '').includes('png') ? 'png'
    : (item.mimeType || '').includes('webp') ? 'webp'
    : 'jpg'
  if (item.kind === 'incident') {
    // PR #153 (B-3) : path stable run-{runId} si runId fourni (source='run')
    if (item.runId) {
      return `${item.logementId}/incidents/run-${item.runId}/${item.id}.${ext}`
    }
    // Sinon admin manuel : item.pieceId = incident.id final (post-INSERT)
    return `${item.logementId}/incidents/admin-${item.pieceId}/${item.id}.${ext}`
  }
  return `${item.logementId}/runs/${item.runId}/${item.pieceId}-${item.id}.${ext}`
}

// ── PR #153 (R-1 fix) — Mini-queue incident pré-INSERT ménage ───────

function extFromMime(mimeType) {
  return (mimeType || '').includes('png') ? 'png'
    : (mimeType || '').includes('webp') ? 'webp'
    : 'jpg'
}

/**
 * Construit le path stable d'une photo incident pré-INSERT (mini-queue
 * ménage). Inclut un segment tmpIncidentId pour permettre cleanup ciblé
 * en cas de cancel modal sans casser les autres incidents du même run.
 *
 * Format : {logementId}/incidents/run-{runId}/incident-{tmpIncidentId}/{ts}.{ext}
 *
 * Note : diverge du buildStoragePath legacy qui agrège toutes les photos
 * d'un même run sous run-{runId}/. Ce path par-incident est nécessaire
 * pour le cleanup sélectif au cancel.
 */
export function buildIncidentPrePath({ logementId, runId, tmpIncidentId, timestamp, mimeType }) {
  const ext = extFromMime(mimeType)
  const ts = timestamp || Date.now()
  return `${logementId}/incidents/run-${runId}/incident-${tmpIncidentId}/${ts}.${ext}`
}

/**
 * Upload une photo incident vers Storage AVANT que l'incident existe
 * en DB (mini-queue pré-INSERT côté ménage).
 *
 * Retry x3 sur erreur réseau (sans backoff long — UX synchrone modal).
 * Si 3 échecs consécutifs → throw pour remontée UI (utilisateur retry
 * manuellement ou abandonne).
 *
 * @param {Object} params
 * @param {string} params.logementId
 * @param {string} params.runId
 * @param {string} params.tmpIncidentId — UUID client, 1 par session modal
 * @param {Blob} params.file
 * @returns {Promise<{path: string, publicUrl: string|null, fileName: string}>}
 */
export async function enqueueIncidentPhoto({ logementId, runId, tmpIncidentId, file }) {
  if (!logementId) throw new Error('enqueueIncidentPhoto: logementId requis')
  if (!runId) throw new Error('enqueueIncidentPhoto: runId requis')
  if (!tmpIncidentId) throw new Error('enqueueIncidentPhoto: tmpIncidentId requis')
  if (!file) throw new Error('enqueueIncidentPhoto: file requis')

  // ⚠️ L'ORIGINAL D'ABORD (refonte Incidents, GO Dimitri 2026-08-13). Une photo
  // de preuve VAUT par ses EXIF (date de prise de vue, appareil) — et la
  // compression canvas les STRIP (elle redessine les pixels). Doctrine :
  //   1. EXTRAIRE date/appareil de l'ORIGINAL, avant toute compression — ils
  //      survivent en base même si le repli compressé s'impose ;
  //   2. ≤ 8 MB → uploader l'ORIGINAL tel quel (EXIF préservés dans le fichier,
  //      le bucket menage-photos n'a pas de limite de taille — mesuré) ;
  //   3. > 8 MB, ou ÉCHEC RÉSEAU de l'original → repli COMPRESSÉ (une preuve
  //      compressée vaut mieux que pas de preuve).
  const SEUIL_ORIGINAL = 8 * 1024 * 1024
  const exif = await extraireExif(file) // fail-soft { date: null, appareil: null }

  const originalEligible = file.size <= SEUIL_ORIGINAL
  let blobToUpload = file
  let compressedMimeType = file.type || 'image/jpeg'
  let originale = originalEligible

  const compresser = async () => {
    try {
      const result = await compressImage(file)
      if (result.compressed) {
        // eslint-disable-next-line no-console
        console.info(
          `[enqueueIncidentPhoto] Compression : ${formatSize(file.size)} -> ${formatSize(result.finalSize)}`
        )
        return { blob: result.blob, mime: 'image/jpeg' }
      }
      return { blob: result.blob, mime: file.type || 'image/jpeg' }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        `[enqueueIncidentPhoto] Compression échouée, on garde l'original (best-effort) : ${err?.message || err}`
      )
      return { blob: file, mime: file.type || 'image/jpeg' }
    }
  }

  if (!originalEligible) {
    const c = await compresser()
    blobToUpload = c.blob
    compressedMimeType = c.mime
    originale = false
  }

  const path = buildIncidentPrePath({
    logementId,
    runId,
    tmpIncidentId,
    timestamp: Date.now(),
    mimeType: compressedMimeType,
  })

  let lastErr = null
  for (let attempt = 1; attempt <= INCIDENT_UPLOAD_MAX_ATTEMPTS; attempt++) {
    try {
      const { error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(path, blobToUpload, { contentType: compressedMimeType, upsert: false })
      if (error) throw error
      // Bucket PRIVÉ (ADR-209) : pas d'URL publique. On stocke le `path` (signé à
      // l'affichage via l'endpoint incident_photos / handleProprioIncidents).
      // L'aperçu immédiat dans la modale passe par un objectURL local (appelant).
      return {
        path,
        publicUrl: null,
        fileName: file.name || path.split('/').pop(),
        // Métadonnées de PREUVE — extraites de l'ORIGINAL avant compression.
        exifDate: exif.date,
        exifAppareil: exif.appareil,
        originale,
      }
    } catch (err) {
      lastErr = err
      // ⚠️ LE REPLI COMPRESSÉ SUR ÉCHEC RÉSEAU : si l'ORIGINAL n'est pas passé
      // à mi-parcours des tentatives (connexion faible sur le terrain), on
      // bascule sur le compressé — plus léger, plus de chances de passer. Les
      // EXIF du fichier sont perdus, mais date/appareil survivent (extraits).
      if (originale && attempt >= Math.ceil(INCIDENT_UPLOAD_MAX_ATTEMPTS / 2)) {
        const c = await compresser()
        blobToUpload = c.blob
        compressedMimeType = c.mime
        originale = false
      }
      // Pas de backoff long ici (UX synchrone modale). Petit délai
      // 200ms entre tentatives pour laisser passer un blip réseau bref.
      if (attempt < INCIDENT_UPLOAD_MAX_ATTEMPTS) {
        await new Promise(r => setTimeout(r, 200))
      }
    }
  }
  throw new Error(`Upload photo échoué après ${INCIDENT_UPLOAD_MAX_ATTEMPTS} tentatives : ${lastErr?.message || lastErr}`)
}

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / 1024 / 1024).toFixed(2) + ' MB'
}

/**
 * Best-effort delete d'une seule photo Storage (suppression individuelle
 * dans la galerie miniatures côté UI).
 * Tolérant aux erreurs (try/catch swallow + console.warn).
 */
export async function removeIncidentPhoto(path) {
  if (!path) return false
  try {
    const { error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([path])
    if (error) throw error
    return true
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[removeIncidentPhoto] best-effort fail:', err?.message || err)
    return false
  }
}

/**
 * Cleanup best-effort des photos uploadées avant un cancel modal.
 * Liste les paths matchant le préfixe `incident-{tmpIncidentId}/` puis
 * batch DELETE. Tolérant aux erreurs (try/catch swallow + console.warn).
 *
 * Crash/refresh navigateur → orphelines avec tmpIncidentId restent en
 * Storage et seront nettoyées par BACKLOG-5 (cleanup mensuel cron).
 *
 * @param {Object} params
 * @param {string} params.logementId
 * @param {string} params.runId
 * @param {string} params.tmpIncidentId
 * @returns {Promise<{deleted: number}>}
 */
export async function cleanupIncidentPhotos({ logementId, runId, tmpIncidentId }) {
  if (!logementId || !runId || !tmpIncidentId) {
    return { deleted: 0 }
  }
  const folder = `${logementId}/incidents/run-${runId}/incident-${tmpIncidentId}`
  try {
    const { data: files, error: listErr } = await supabase.storage
      .from(STORAGE_BUCKET)
      .list(folder, { limit: 100 })
    if (listErr) throw listErr
    if (!files || files.length === 0) return { deleted: 0 }
    const paths = files.map(f => `${folder}/${f.name}`)
    const { error: removeErr } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove(paths)
    if (removeErr) throw removeErr
    return { deleted: paths.length }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[cleanupIncidentPhotos] best-effort fail:', err?.message || err)
    return { deleted: 0 }
  }
}
